import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import apiClient from "../../core/config/api";
import { formatMoneyWithCode } from "../../utils/currency";
import "./paymenthistory.css";

type PaymentItem = {
  id: number;
  plan_id?: number | null;
  status: string; // success | failed | cancelled | pending
  amount: string | number;
  total_amount: string | number;
  currency: string;
  payment_method: string;
  session_id?: string | null;
  subscription_id?: string | null;
  invoice_url?: string | null;
  created_at?: string | null;
};

type PaymentListResponse = {
  success: boolean;
  count: number;
  payments: PaymentItem[];
};

type SubscriptionResponse = {
  success: boolean;
  has_subscription: boolean;
  plan_id?: number | null;
  plan_name?: string | null;
  billing_interval?: string | null; // "monthly" | "yearly"
  price?: string | number | null;
  currency?: string | null;
  status?: string | null; // "active" | "cancelled"
  subscription_id?: string | null;
  next_renewal_date?: string | null; // ISO
};

type CreditsResponse = {
  success: boolean;
  allocated_credits: number;
  bonus_credits: number;
  used_credits: number;
  total_credits: number;
  available_credits: number;
  billing_period?: string | null;
  valid_until?: string | null;
};

const STATUS_STYLES: Record<string, { label: string; color: string; bg: string }> = {
  success: { label: "Paid", color: "#0a7d33", bg: "#e6f5ec" },
  pending: { label: "Pending", color: "#9a6700", bg: "#fff6e0" },
  failed: { label: "Failed", color: "#b42318", bg: "#fdeceb" },
  cancelled: { label: "Cancelled", color: "#6b7280", bg: "#f1f2f4" },
};

type InvoiceUrlResponse = {
  success: boolean;
  payment_id: number;
  invoice_url?: string | null;
  invoice_pdf?: string | null;
};

const PaymentHistory = () => {
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [credits, setCredits] = useState<CreditsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoiceLoading, setInvoiceLoading] = useState<Set<number>>(new Set());
  const [invoiceErrors, setInvoiceErrors] = useState<Record<number, string>>({});
  const navigate = useNavigate();
  // Members (role 6) use their account owner's plan, so they can't upgrade it.
  const isMember = Number(useSelector((state: RootState) => state.auth.roleId)) === 6;

  const handleDownloadInvoice = async (paymentId: number) => {
    setInvoiceLoading((prev) => new Set(prev).add(paymentId));
    setInvoiceErrors((prev) => { const next = { ...prev }; delete next[paymentId]; return next; });

    try {
      const { data } = await apiClient.get<InvoiceUrlResponse>(
        `/stripe-payment/payments/${paymentId}/invoice`
      );
      const url = data?.invoice_pdf || data?.invoice_url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        setInvoiceErrors((prev) => ({ ...prev, [paymentId]: "Invoice not available yet." }));
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Failed to fetch invoice.";
      setInvoiceErrors((prev) => ({ ...prev, [paymentId]: msg }));
    } finally {
      setInvoiceLoading((prev) => {
        const next = new Set(prev);
        next.delete(paymentId);
        return next;
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setError("");

      const [paymentsRes, subRes, creditsRes] = await Promise.allSettled([
        apiClient.get<PaymentListResponse>("/stripe-payment/payments"),
        apiClient.get<SubscriptionResponse>("/stripe-payment/subscription"),
        apiClient.get<CreditsResponse>("/credit-usage/me"),
      ]);

      if (cancelled) return;

      if (paymentsRes.status === "fulfilled") {
        const data = paymentsRes.value.data;
        setPayments(Array.isArray(data?.payments) ? data.payments : []);
      } else {
        setError(
          paymentsRes.reason?.response?.data?.detail ||
            paymentsRes.reason?.response?.data?.message ||
            "Failed to load payment history."
        );
      }

      if (subRes.status === "fulfilled") setSubscription(subRes.value.data);
      if (creditsRes.status === "fulfilled") setCredits(creditsRes.value.data);

      setLoading(false);
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // Summary cards derived from the real payment list.
  // Per currency: USD and INR (India pricing) payments are never added together.
  const { totalTransactions, totalsText, averagesText } = useMemo(() => {
    const successful = payments.filter((p) => p.status === "success");
    const groups = new Map<string, { total: number; count: number }>();
    successful.forEach((p) => {
      const code = (p.currency || "USD").toUpperCase();
      const group = groups.get(code) || { total: 0, count: 0 };
      group.total += Number(p.total_amount) || 0;
      group.count += 1;
      groups.set(code, group);
    });
    const list = groups.size ? [...groups.entries()] : [["USD", { total: 0, count: 0 }] as const];
    return {
      totalTransactions: payments.length,
      totalsText: list.map(([code, g]) => formatMoneyWithCode(g.total, code)).join(" + "),
      averagesText: list.map(([code, g]) => formatMoneyWithCode(g.count ? g.total / g.count : 0, code)).join(" / "),
    };
  }, [payments]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB"); // dd/mm/yyyy to match prior format
  };

  const formatMoney = (amount: number, currency = "USD") => formatMoneyWithCode(amount, currency);

  const titleCase = (val?: string | null) =>
    val ? val.charAt(0).toUpperCase() + val.slice(1) : "";

  const formatInterval = (val?: string | null) => {
    if (!val) return "";
    if (val.toLowerCase() === "one_time") return "One-time";
    return val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, " ");
  };

  const planLabel = (() => {
    if (loading) return "Loading…";
    if (!subscription?.has_subscription) return "No active plan";
    const interval = subscription.billing_interval
      ? ` (${formatInterval(subscription.billing_interval)})`
      : "";
    const cancelled = subscription.status === "cancelled" ? " — Cancelled" : "";
    return `${subscription.plan_name || "Subscription"}${interval}${cancelled}`;
  })();

  const renderStatus = (status: string) => {
    const s = STATUS_STYLES[status] || {
      label: status || "—",
      color: "#6b7280",
      bg: "#f1f2f4",
    };
    return (
      <span
        className="paid"
        style={{
          color: s.color,
          backgroundColor: s.bg,
          padding: "2px 10px",
          borderRadius: "12px",
          fontWeight: 600,
          fontSize: "12px",
          display: "inline-block",
        }}
      >
        {s.label}
      </span>
    );
  };

  return (
    <>
      <div className="dashboard-page">
        <h1>Payment History</h1>
        <p>View your complete transaction records and download invoices</p>
      </div>

      <div className="row">
        <div className="col-md-10">
          <div className="user-management-box">
            <div className="pay-box">
              <div className="d-flex flex-row justify-content-between">
                <div>
                  <p className="pay-as-you-go">
                    <span className="current-plan-inline">Current Plan:</span>{" "}
                    <span className="plan-name-highlight">{planLabel}</span>
                    {!loading && subscription?.has_subscription && (
                      <span className="next-renewal-inline">
                        {" "}
                        (Next Renewal: {formatDate(subscription?.next_renewal_date)})
                      </span>
                    )}
                  </p>
                </div>
                {!isMember && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="add-users"
                      onClick={() => navigate("/pricing")}
                    >
                      Upgrade Plan
                    </button>
                  </div>
                )}
              </div>
            </div>

            <p className="current-plan">Invoice History</p>

            <div className="table-wrapper table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Invoice Date</th>
                    <th>Invoice ID</th>
                    <th>Payment Status</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center" }}>
                        Loading payments…
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", color: "#b42318" }}>
                        {error}
                      </td>
                    </tr>
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center" }}>
                        No payments yet.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p) => (
                      <tr key={p.id}>
                        <td>{formatDate(p.created_at)}</td>
                        <td>{p.session_id || p.id}</td>
                        <td>{renderStatus(p.status)}</td>
                        <td>{formatMoney(Number(p.total_amount) || 0, p.currency)}</td>
                        <td>
                          <button
                            type="button"
                            className="add-users"
                            // A manually recorded payment has no Stripe invoice behind
                            // it, so the button would only ever return an error.
                            disabled={
                              p.status !== "success" ||
                              (p.payment_method === "manual" && !p.invoice_url) ||
                              invoiceLoading.has(p.id)
                            }
                            title={
                              p.payment_method === "manual" && !p.invoice_url
                                ? "No invoice was issued for this payment"
                                : undefined
                            }
                            onClick={() => handleDownloadInvoice(p.id)}
                          >
                            {invoiceLoading.has(p.id) ? "Downloading…" : "Download Invoice"}
                          </button>
                          {invoiceErrors[p.id] && (
                            <div style={{ color: "#b42318", fontSize: "11px", marginTop: "4px" }}>
                              {invoiceErrors[p.id]}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="user-management-box-1">
            <p className="total-credits">Total Transaction</p>
            <span className="total-credits">{loading ? "—" : totalTransactions}</span>
            <div className="devider"></div>
            <p className="total-credits">Total Amount</p>
            <span className="total-credits">
              {loading ? "—" : totalsText}
            </span>
            <div className="devider"></div>
            <p className="total-credits">Total Credits</p>
            <span className="total-credits-r">
              {loading ? "—" : (credits?.total_credits ?? 0).toLocaleString()}
            </span>
            <div className="devider"></div>
            <p className="total-credits">Avg. Value</p>
            <span className="total-credits-m">
              {loading ? "—" : averagesText}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentHistory;
